/**
 * @file contactHelpers.js
 * @description Helpers para el módulo de contactos (extraídos de ContactsView).
 * @author SIVOL® - Aldo Frias - Argentina
 */

import { construirUrlImagen } from './helpers';
import { resolveAvatar as resolveAvatarCanonical } from './avatarResolver';
import { appLangToBcp47 } from '../i18n/appIntlLocale.js';
import { COOLDOWN_STORAGE_KEY, DAILY_RESEND_STORAGE_KEY, DAILY_RESEND_MAX, RESEND_COOLDOWN_MS } from './constants';
import { isAvatarTraceEnabled } from './avatarTrace';
import { devLog } from './devConsoleLog';

const INVALID_MERGE_VALUES = new Set(['', 'null', 'undefined', '[object object]']);
const AVATAR_FALLBACK_MARKERS = [
  '/assets/default-avatar.png',
  'default-avatar.png',
  '/logoarchat.png',
  'logoarchat.png',
  '/logoarchat.jpg',
  'logoarchat.jpg',
];

function isMeaningfulValue(value) {
  if (value === undefined || value === null) return false;
  const normalized = String(value).trim();
  if (!normalized) return false;
  return !INVALID_MERGE_VALUES.has(normalized.toLowerCase());
}

function isFallbackAvatarValue(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  return AVATAR_FALLBACK_MARKERS.some((marker) => normalized.endsWith(marker));
}

function pickFirstFilled(...values) {
  return values.find((value) => isMeaningfulValue(value));
}

/**
 * true si el texto de preview de último mensaje es mostrable (no vacío, no solo elipsis de UI).
 * @param {*} value
 * @returns {boolean}
 */
export function isMeaningfulLastMessagePreview(value) {
  if (!isMeaningfulValue(value)) return false;
  const s = String(value).trim();
  if (s === '...' || s === '\u2026') return false;
  return true;
}

function parseLastMessageTs(v) {
  if (v == null) return 0;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

function hasIncomingLastMessageText(incoming) {
  if (!incoming || typeof incoming !== 'object') return false;
  return (
    isMeaningfulLastMessagePreview(incoming.ultimo_mensaje_preview) ||
    isMeaningfulLastMessagePreview(incoming.ultimo_mensaje) ||
    isMeaningfulLastMessagePreview(incoming.last_message_preview) ||
    isMeaningfulLastMessagePreview(incoming.lastMessage) ||
    isMeaningfulLastMessagePreview(incoming.contenido) ||
    isMeaningfulLastMessagePreview(incoming.contenido_original) ||
    isMeaningfulLastMessagePreview(incoming.mensaje)
  );
}

/**
 * Mantiene cuerpos crudos (API a veces solo en `contenido` / `mensaje`) al fusionar.
 * @param {object} out
 * @param {object} existing
 * @param {object} incoming
 */
function copyPreservedMessageBodyFields(out, existing, incoming) {
  for (const k of ['contenido', 'contenido_original', 'mensaje']) {
    if (isMeaningfulLastMessagePreview(existing?.[k]) && !isMeaningfulLastMessagePreview(incoming?.[k])) {
      out[k] = existing[k];
    }
  }
}

/**
 * Tipo "texto/chat" sin cuerpo no cuenta como media sustituta en el merge: la API
 * a menudo trae `tipo: texto` y preview null y eso hacía inOk=true y ganaba al socket (caso D).
 */
function isNonTextoLastMessageTipo(incoming) {
  const t = String(
    incoming?.ultimo_mensaje_tipo ?? incoming?.last_message_type ?? incoming?.message_type ?? ''
  )
    .toLowerCase()
    .trim();
  if (t === '' || t === 'text' || t === 'texto' || t === 'chat' || t === 'sistema' || t === 'system') {
    return false;
  }
  if (
    t.includes('image') ||
    t.includes('imagen') ||
    t === 'photo' ||
    t.includes('audio') ||
    t.includes('voice') ||
    t.includes('voz') ||
    t === 'video' ||
    t.includes('file') ||
    t.includes('archivo') ||
    t.includes('document')
  ) {
    return true;
  }
  return t !== '' && t !== 'text' && t !== 'texto';
}

/** Fila remota sin subtítulo mostrable real: no debe poner el preview bueno de estado local. */
function isVacuousLastMessageForMerge(incoming) {
  if (!incoming || typeof incoming !== 'object') return true;
  if (hasIncomingLastMessageText(incoming)) return false;
  if (isNonTextoLastMessageTipo(incoming)) return false;
  return true;
}

/** Hay qué conservar: texto o media con tipo Foto/Audio/… (sin depender de lo que ponga la API vacía). */
function hasLastMessageDisplayToPreserve(existing) {
  if (!existing || typeof existing !== 'object') return false;
  if (hasIncomingLastMessageText(existing)) return true;
  return isNonTextoLastMessageTipo(existing);
}

/**
 * Dato "fuerte" para decidir inOk/exOk: texto real, o tipo distinto de texto/vacío (Foto, Audio, …).
 * `tipo: texto` sin cuerpo NO es fuerte — si no, inOk queda true con API vacía y gana al socket.
 */
function hasStrongLastMessageData(row) {
  if (!row || typeof row !== 'object') return false;
  if (row.no_visible_last_message === true) return true;
  if (row.last_message_cleared === true) return true;
  if (hasIncomingLastMessageText(row)) return true;
  if (isNonTextoLastMessageTipo(row)) return true;
  return false;
}

/**
 * Al fusionar filas de contacto (API vs estado previo): nunca pisa un preview/último bueno
 * con null, "", "..." o ausencia de campos salvo señal explícita de limpieza.
 * @param {object|null|undefined} existing
 * @param {object|null|undefined} incoming
 * @returns {object}
 */
export function mergeContactLastMessagePreserve(existing, incoming) {
  if (!incoming || typeof incoming !== 'object') {
    return existing && typeof existing === 'object' ? { ...existing } : {};
  }
  if (!existing || typeof existing !== 'object') {
    return { ...incoming };
  }
  if (incoming.no_visible_last_message === true) {
    return {
      ...existing,
      ...incoming,
      ultimo_mensaje_preview: null,
      ultimo_mensaje: null,
      ultimo_mensaje_tipo: null,
      ultimo_mensaje_id: null,
      fecha_ultimo_mensaje: null,
      last_message_preview: null,
      lastMessage: null,
      last_subject_id: null,
      last_subject_title: null,
      last_subject_is_default: undefined,
      last_subject_has_explicit_subject: 0,
    };
  }
  if (incoming.last_message_cleared === true) {
    return {
      ...existing,
      ...incoming,
      ultimo_mensaje_preview: null,
      ultimo_mensaje: null,
      last_message_preview: null,
      lastMessage: null,
      last_subject_id: null,
      last_subject_title: null,
      last_subject_is_default: undefined,
      last_subject_has_explicit_subject: 0,
    };
  }

  const out = { ...existing, ...incoming };
  const exOk = hasStrongLastMessageData(existing);
  const inOk = hasStrongLastMessageData(incoming);
  if (!inOk && exOk) {
    out.ultimo_mensaje_preview = existing.ultimo_mensaje_preview;
    out.ultimo_mensaje = existing.ultimo_mensaje ?? existing.ultimo_mensaje_preview;
    if (existing.last_message_preview != null && (incoming.last_message_preview == null || !isMeaningfulLastMessagePreview(incoming.last_message_preview))) {
      out.last_message_preview = existing.last_message_preview;
    }
    if (existing.lastMessage != null && String(incoming?.lastMessage ?? '').trim() === '' && isMeaningfulLastMessagePreview(existing.lastMessage)) {
      out.lastMessage = existing.lastMessage;
    }
    if (String(incoming.ultimo_mensaje_tipo ?? '').trim() === '' && String(existing.ultimo_mensaje_tipo ?? '').trim() !== '') {
      out.ultimo_mensaje_tipo = existing.ultimo_mensaje_tipo;
    }
    if (
      (incoming.ultimo_mensaje_id == null || incoming.ultimo_mensaje_id === '') &&
      existing.ultimo_mensaje_id != null &&
      existing.ultimo_mensaje_id !== ''
    ) {
      out.ultimo_mensaje_id = existing.ultimo_mensaje_id;
    }
    if (!incoming.fecha_ultimo_mensaje && existing.fecha_ultimo_mensaje) {
      out.fecha_ultimo_mensaje = existing.fecha_ultimo_mensaje;
    }
    copyPreservedMessageBodyFields(out, existing, incoming);
    attachLastSubjectFieldsAfterMerge(out, existing, incoming);
    return out;
  }
  if (exOk && inOk) {
    const idIn = incoming.ultimo_mensaje_id;
    const idEx = existing.ultimo_mensaje_id;
    const nIn = idIn != null && String(idIn).trim() !== '' ? Number(idIn) : NaN;
    const nEx = idEx != null && String(idEx).trim() !== '' ? Number(idEx) : NaN;
    const idsComparable = Number.isFinite(nIn) && Number.isFinite(nEx) && nIn > 0 && nEx > 0;
    if (idsComparable && nIn < nEx) {
      out.ultimo_mensaje_preview = existing.ultimo_mensaje_preview;
      out.ultimo_mensaje = existing.ultimo_mensaje ?? existing.ultimo_mensaje_preview;
      out.ultimo_mensaje_tipo = existing.ultimo_mensaje_tipo;
      out.ultimo_mensaje_id = existing.ultimo_mensaje_id;
      out.fecha_ultimo_mensaje = existing.fecha_ultimo_mensaje;
      if (existing.last_message_preview) out.last_message_preview = existing.last_message_preview;
      if (isMeaningfulLastMessagePreview(existing.lastMessage)) out.lastMessage = existing.lastMessage;
    } else if (!idsComparable) {
      const tsIn = parseLastMessageTs(incoming.fecha_ultimo_mensaje);
      const tsEx = parseLastMessageTs(existing.fecha_ultimo_mensaje);
      if (tsEx > 0 && tsIn > 0 && tsIn < tsEx) {
        out.ultimo_mensaje_preview = existing.ultimo_mensaje_preview;
        out.ultimo_mensaje = existing.ultimo_mensaje ?? existing.ultimo_mensaje_preview;
        out.ultimo_mensaje_tipo = existing.ultimo_mensaje_tipo;
        out.ultimo_mensaje_id = existing.ultimo_mensaje_id;
        out.fecha_ultimo_mensaje = existing.fecha_ultimo_mensaje;
        if (existing.last_message_preview) out.last_message_preview = existing.last_message_preview;
        if (isMeaningfulLastMessagePreview(existing.lastMessage)) out.lastMessage = existing.lastMessage;
      }
    }
  }
  // Mismo mensaje (id) pero API sin preview: no pisar texto ya mostrado (caso A).
  const exText = hasIncomingLastMessageText(existing);
  const inText = hasIncomingLastMessageText(incoming);
  if (exText && !inText) {
    const idIn0 = incoming.ultimo_mensaje_id;
    const idEx0 = existing.ultimo_mensaje_id;
    const nIn0 = idIn0 != null && String(idIn0).trim() !== '' ? Number(idIn0) : NaN;
    const nEx0 = idEx0 != null && String(idEx0).trim() !== '' ? Number(idEx0) : NaN;
    if (Number.isFinite(nIn0) && Number.isFinite(nEx0) && nIn0 > 0 && nEx0 > 0 && nIn0 === nEx0) {
      out.ultimo_mensaje_preview = existing.ultimo_mensaje_preview;
      out.ultimo_mensaje = existing.ultimo_mensaje ?? existing.ultimo_mensaje_preview;
      if (existing.last_message_preview) out.last_message_preview = existing.last_message_preview;
      if (isMeaningfulLastMessagePreview(existing.lastMessage)) out.lastMessage = existing.lastMessage;
    }
  }
  if (incoming.no_visible_last_message !== true && incoming.last_message_cleared !== true) {
    copyPreservedMessageBodyFields(out, existing, incoming);
  }
  // D: defensa adicional — API aún "fuerte" por otro camino pero sin preview útil.
  if (
    incoming.no_visible_last_message !== true &&
    incoming.last_message_cleared !== true &&
    isVacuousLastMessageForMerge(incoming) &&
    hasLastMessageDisplayToPreserve(existing)
  ) {
    out.ultimo_mensaje_preview = existing.ultimo_mensaje_preview;
    out.ultimo_mensaje = existing.ultimo_mensaje ?? existing.ultimo_mensaje_preview;
    out.ultimo_mensaje_tipo = existing.ultimo_mensaje_tipo;
    out.ultimo_mensaje_id = existing.ultimo_mensaje_id;
    out.fecha_ultimo_mensaje = existing.fecha_ultimo_mensaje;
    if (existing.last_message_preview) {
      out.last_message_preview = existing.last_message_preview;
    }
    if (isMeaningfulLastMessagePreview(existing.lastMessage)) {
      out.lastMessage = existing.lastMessage;
    }
    for (const k of ['contenido', 'contenido_original', 'mensaje']) {
      if (isMeaningfulLastMessagePreview(existing?.[k])) {
        out[k] = existing[k];
      }
    }
    if (existing.last_subject_id !== undefined) out.last_subject_id = existing.last_subject_id;
    if (existing.last_subject_title !== undefined) out.last_subject_title = existing.last_subject_title;
    if (existing.last_subject_is_default !== undefined) out.last_subject_is_default = existing.last_subject_is_default;
    if (existing.last_subject_has_explicit_subject !== undefined) {
      out.last_subject_has_explicit_subject = existing.last_subject_has_explicit_subject;
    }
  }
  attachLastSubjectFieldsAfterMerge(out, existing, incoming);
  return out;
}

/**
 * Alinea metadatos de asunto (último mensaje) con el ultimo_mensaje_id efectivo tras el merge.
 * @param {object} out
 * @param {object} existing
 * @param {object} incoming
 */
function attachLastSubjectFieldsAfterMerge(out, existing, incoming) {
  const widRaw = out?.ultimo_mensaje_id;
  const wid = widRaw != null && String(widRaw).trim() !== '' ? Number(widRaw) : NaN;
  if (!Number.isFinite(wid) || wid <= 0) {
    delete out.last_subject_id;
    delete out.last_subject_title;
    delete out.last_subject_is_default;
    delete out.last_subject_has_explicit_subject;
    return;
  }
  const inId = incoming?.ultimo_mensaje_id != null && String(incoming.ultimo_mensaje_id).trim() !== ''
    ? Number(incoming.ultimo_mensaje_id)
    : NaN;
  const exId = existing?.ultimo_mensaje_id != null && String(existing.ultimo_mensaje_id).trim() !== ''
    ? Number(existing.ultimo_mensaje_id)
    : NaN;
  const inMatch = Number.isFinite(inId) && inId === wid;
  const exMatch = Number.isFinite(exId) && exId === wid;
  const metaSource =
    inMatch && exMatch ? incoming : inMatch ? incoming : exMatch ? existing : null;
  if (!metaSource) {
    delete out.last_subject_id;
    delete out.last_subject_title;
    delete out.last_subject_is_default;
    delete out.last_subject_has_explicit_subject;
    return;
  }
  if (metaSource.last_subject_id !== undefined) out.last_subject_id = metaSource.last_subject_id;
  if (metaSource.last_subject_title !== undefined) out.last_subject_title = metaSource.last_subject_title;
  if (metaSource.last_subject_is_default !== undefined) out.last_subject_is_default = metaSource.last_subject_is_default;
  if (metaSource.last_subject_has_explicit_subject !== undefined) {
    out.last_subject_has_explicit_subject = metaSource.last_subject_has_explicit_subject;
  }
}

function pickFirstAvatar(...values) {
  const meaningful = values.filter((value) => isMeaningfulValue(value));
  if (!meaningful.length) return undefined;
  const nonFallback = meaningful.find((value) => !isFallbackAvatarValue(value));
  return nonFallback ?? meaningful[0];
}

/**
 * Normaliza una entidad de contacto consolidando fuentes anidadas sin perder campos validos.
 * Fuente canónica: item -> item.contact -> item.user -> item.participant
 * @param {Object} item
 * @returns {Object}
 */
export function normalizeContact(item) {
  if (!item || typeof item !== 'object') return {};
  const contact = item?.contact || {};
  const user = item?.user || {};
  const participant = item?.participant || {};
  const merged = {
    ...item,
    ...contact,
    ...user,
    ...participant,
  };
  const normalized = {
    ...merged,
    // Priorizar primer valor útil para evitar que una rama anidada vacía pise nombre válido.
    nombre_display: pickFirstFilled(item?.nombre_display, contact?.nombre_display, user?.nombre_display, participant?.nombre_display, merged?.nombre_display),
    alias: pickFirstFilled(item?.alias, contact?.alias, user?.alias, participant?.alias, merged?.alias),
    nick: pickFirstFilled(item?.nick, contact?.nick, user?.nick, participant?.nick, merged?.nick),
    nombre: pickFirstFilled(item?.nombre, contact?.nombre, user?.nombre, participant?.nombre, merged?.nombre),
    username: pickFirstFilled(item?.username, contact?.username, user?.username, participant?.username, merged?.username),
    usuario_contacto_id: pickFirstFilled(item?.usuario_contacto_id, contact?.usuario_contacto_id, user?.usuario_contacto_id, participant?.usuario_contacto_id, merged?.usuario_contacto_id),
    contacto_id: pickFirstFilled(item?.contacto_id, contact?.contacto_id, user?.contacto_id, participant?.contacto_id, merged?.contacto_id),
    otro_usuario_id: pickFirstFilled(item?.otro_usuario_id, contact?.otro_usuario_id, user?.otro_usuario_id, participant?.otro_usuario_id, merged?.otro_usuario_id),
    usuario_id: pickFirstFilled(item?.usuario_id, contact?.usuario_id, user?.usuario_id, participant?.usuario_id, merged?.usuario_id),
    id: pickFirstFilled(item?.id, contact?.id, user?.id, participant?.id, merged?.id),
    profile_avatar_full_url: pickFirstFilled(item?.profile_avatar_full_url, contact?.profile_avatar_full_url, user?.profile_avatar_full_url, participant?.profile_avatar_full_url, merged?.profile_avatar_full_url),
    profile_avatar_url: pickFirstFilled(item?.profile_avatar_url, contact?.profile_avatar_url, user?.profile_avatar_url, participant?.profile_avatar_url, merged?.profile_avatar_url),
    avatar_url: pickFirstAvatar(item?.avatar_url, contact?.avatar_url, user?.avatar_url, participant?.avatar_url, merged?.avatar_url),
    avatar: pickFirstAvatar(item?.avatar, contact?.avatar, user?.avatar, participant?.avatar, merged?.avatar),
    foto_perfil: pickFirstAvatar(item?.foto_perfil, contact?.foto_perfil, user?.foto_perfil, participant?.foto_perfil, merged?.foto_perfil),
    foto_url: pickFirstAvatar(item?.foto_url, contact?.foto_url, user?.foto_url, participant?.foto_url, merged?.foto_url),
    avatar_display: pickFirstAvatar(item?.avatar_display, contact?.avatar_display, user?.avatar_display, participant?.avatar_display, merged?.avatar_display),
    avatar_version: pickFirstFilled(item?.avatar_version, contact?.avatar_version, user?.avatar_version, participant?.avatar_version, merged?.avatar_version),
    email: pickFirstFilled(item?.email, contact?.email, user?.email, participant?.email, merged?.email),
    // Fila invitaciones: el id de cancelación/reenvío es invitacion_id, no el usuario invitado.
    invitacion_id: pickFirstFilled(
      item?.invitacion_id,
      contact?.invitacion_id,
      user?.invitacion_id,
      participant?.invitacion_id,
      merged?.invitacion_id
    ),
    invitation_id: pickFirstFilled(
      item?.invitation_id,
      contact?.invitation_id,
      merged?.invitation_id,
      item?.invitacion_id,
      merged?.invitacion_id
    ),
    ultimo_mensaje_preview: pickFirstFilled(
      item?.ultimo_mensaje_preview,
      item?.ultimo_mensaje,
      item?.last_message_preview,
      item?.lastMessagePreview,
      item?.mensaje,
      item?.contenido_original,
      item?.contenido,
      contact?.ultimo_mensaje_preview,
      contact?.ultimo_mensaje,
      contact?.mensaje,
      contact?.contenido_original,
      contact?.contenido,
      user?.ultimo_mensaje_preview,
      user?.ultimo_mensaje,
      user?.mensaje,
      user?.contenido_original,
      participant?.ultimo_mensaje_preview,
      participant?.ultimo_mensaje,
      merged?.ultimo_mensaje_preview,
      merged?.ultimo_mensaje,
      merged?.mensaje,
      merged?.contenido_original,
      merged?.contenido
    ),
    ultimo_mensaje_tipo: pickFirstFilled(
      item?.ultimo_mensaje_tipo,
      item?.last_message_type,
      item?.message_type,
      item?.tipo,
      contact?.ultimo_mensaje_tipo,
      contact?.last_message_type,
      contact?.message_type,
      user?.ultimo_mensaje_tipo,
      merged?.ultimo_mensaje_tipo,
      merged?.last_message_type,
      merged?.message_type
    ),
    ultimo_mensaje_id: pickFirstFilled(
      item?.ultimo_mensaje_id,
      contact?.ultimo_mensaje_id,
      merged?.ultimo_mensaje_id
    ),
    fecha_ultimo_mensaje: pickFirstFilled(
      item?.fecha_ultimo_mensaje,
      contact?.fecha_ultimo_mensaje,
      merged?.fecha_ultimo_mensaje
    ),
    ultimo_mensaje_remitente_id: pickFirstFilled(
      item?.ultimo_mensaje_remitente_id,
      contact?.ultimo_mensaje_remitente_id,
      merged?.ultimo_mensaje_remitente_id
    ),
    ultimo_mensaje_delivery_status: pickFirstFilled(
      item?.ultimo_mensaje_delivery_status,
      contact?.ultimo_mensaje_delivery_status,
      merged?.ultimo_mensaje_delivery_status
    ),
    last_subject_id: pickFirstFilled(item?.last_subject_id, contact?.last_subject_id, merged?.last_subject_id),
    last_subject_title: pickFirstFilled(
      item?.last_subject_title,
      contact?.last_subject_title,
      merged?.last_subject_title
    ),
    last_subject_is_default: pickFirstFilled(
      item?.last_subject_is_default,
      contact?.last_subject_is_default,
      merged?.last_subject_is_default
    ),
    last_subject_has_explicit_subject: pickFirstFilled(
      item?.last_subject_has_explicit_subject,
      contact?.last_subject_has_explicit_subject,
      merged?.last_subject_has_explicit_subject
    ),
    idioma_preferido: pickFirstFilled(
      item?.idioma_preferido,
      contact?.idioma_preferido,
      user?.idioma_preferido,
      participant?.idioma_preferido,
      merged?.idioma_preferido
    ),
    language: pickFirstFilled(item?.language, contact?.language, user?.language, merged?.language),
    language_code: pickFirstFilled(
      item?.language_code,
      contact?.language_code,
      user?.language_code,
      merged?.language_code
    ),
    codigo_pais: pickFirstFilled(
      item?.codigo_pais,
      item?.country_iso,
      contact?.codigo_pais,
      contact?.country_iso,
      user?.codigo_pais,
      user?.country_iso,
      merged?.codigo_pais,
      merged?.country_iso
    ),
    country_iso: pickFirstFilled(
      item?.country_iso,
      item?.codigo_pais,
      contact?.country_iso,
      contact?.codigo_pais,
      user?.country_iso,
      merged?.country_iso,
      merged?.codigo_pais
    ),
    plan: pickFirstFilled(item?.plan, contact?.plan, user?.plan, participant?.plan, merged?.plan),
    plan_display: pickFirstFilled(
      item?.plan_display,
      contact?.plan_display,
      user?.plan_display,
      participant?.plan_display,
      merged?.plan_display
    ),
    is_founder:
      item?.is_founder ??
      contact?.is_founder ??
      user?.is_founder ??
      participant?.is_founder ??
      merged?.is_founder,
    founder_position:
      item?.founder_position ??
      contact?.founder_position ??
      user?.founder_position ??
      participant?.founder_position ??
      merged?.founder_position,
  };
  if (isAvatarTraceEnabled()) {
    devLog('[NORMALIZED_CONTACT]', normalized);
  }
  return normalized;
}

/**
 * Etiqueta de plan (FREE / PRO / FOUNDATION) para fila de contacto; usa API o deriva de plan + is_founder.
 * @param {Object|null|undefined} contact
 * @returns {string|null}
 */
export function resolveContactPlanDisplay(contact) {
  if (!contact || typeof contact !== 'object') return null;
  const rawFounder = contact.is_founder;
  const isFounder =
    rawFounder === true ||
    rawFounder === 1 ||
    String(rawFounder ?? '').trim() === '1';
  const posRaw = contact.founder_position;
  const pos =
    posRaw != null && posRaw !== '' && Number.isFinite(Number(posRaw)) ? Number(posRaw) : null;
  const planLower = typeof contact.plan === 'string' ? contact.plan.toLowerCase().trim() : '';
  const isPro = planLower === 'pro' || planLower === 'pro_founder';
  if (isFounder && !isPro) {
    return pos != null ? `FOUNDER #${pos}` : 'FOUNDATION';
  }
  if (isPro) return 'PRO';
  const preset = pickFirstFilled(
    contact.plan_display,
    contact.plan_code,
    contact.commercial_plan
  );
  if (isMeaningfulValue(preset)) {
    return String(preset).trim().toUpperCase();
  }
  const planVal = contact.plan;
  const raw =
    planVal != null && String(planVal).trim() !== ''
      ? String(planVal).toLowerCase().trim()
      : '';
  if (raw === 'foundation') return 'FOUNDATION';
  if (raw === 'pro' || raw === 'pro_founder') return 'PRO';
  if (raw === 'free' || raw === '') return 'FREE';
  return raw.toUpperCase();
}

/**
 * Estrella dorada dentro del pill: FOUNDER #n y FOUNDATION (tier premium).
 * PRO y FREE sin estrella.
 *
 * @param {Object|null|undefined} contact Contacto normalizado
 * @param {string|null|undefined} planLabel Salida de resolveContactPlanDisplay
 * @returns {boolean}
 */
export function shouldShowContactPlanGoldStar(contact, planLabel) {
  if (!planLabel || !contact || typeof contact !== 'object') return false;
  const label = String(planLabel).trim().toUpperCase();
  if (label === 'FREE' || label === 'PRO') return false;
  if (label.startsWith('FOUNDER')) return true;
  if (label === 'FOUNDATION') return true;
  return false;
}

/** Cooldown de invitaciones (localStorage) */
export function getInvitationCooldown(invitationId) {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(COOLDOWN_STORAGE_KEY) : null;
    const data = raw ? JSON.parse(raw) : {};
    const ts = data[String(invitationId)];
    return typeof ts === 'number' ? ts : null;
  } catch {
    return null;
  }
}

/** Guardar cooldown de invitación */
export function setInvitationCooldown(invitationId) {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(COOLDOWN_STORAGE_KEY) : null;
    const data = raw ? JSON.parse(raw) : {};
    data[String(invitationId)] = Date.now();
    if (typeof localStorage !== 'undefined') localStorage.setItem(COOLDOWN_STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

/** Datos de reenvíos diarios (localStorage) */
export function getDailyResendData() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(DAILY_RESEND_STORAGE_KEY) : null;
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** String de fecha actual YYYY-MM-DD */
export function getTodayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Contador de reenvíos diarios por invitación */
export function getDailyResendCount(invitationId) {
  const data = getDailyResendData();
  const entry = data[String(invitationId)];
  if (!entry || entry.date !== getTodayStr()) return 0;
  return typeof entry.count === 'number' ? entry.count : 0;
}

/** Incrementar contador de reenvío diario */
export function incrementDailyResend(invitationId) {
  try {
    const data = getDailyResendData();
    const today = getTodayStr();
    const key = String(invitationId);
    const entry = data[key];
    if (!entry || entry.date !== today) {
      data[key] = { count: 1, date: today };
    } else {
      data[key] = { count: (entry.count || 0) + 1, date: today };
    }
    if (typeof localStorage !== 'undefined') localStorage.setItem(DAILY_RESEND_STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

/** Comprobar si la invitación está en cooldown */
export function isInvitationInCooldown(invitationId, inv) {
  const fromStorage = getInvitationCooldown(invitationId);
  const fromInv = inv ? (inv.last_sent_at ?? inv.sent_at ?? inv.created_at) : null;
  const lastSent = fromStorage != null ? fromStorage : (fromInv ? new Date(fromInv).getTime() : null);
  if (lastSent == null) return false;
  return (Date.now() - lastSent) < RESEND_COOLDOWN_MS;
}

/** Comprobar si se alcanzó el límite diario de reenvíos */
export function isDailyLimitReached(invitationId) {
  return getDailyResendCount(invitationId) >= DAILY_RESEND_MAX;
}

/** Prioridad de avatar en lista de contactos (alineada con PerfilHelper) */
export function resolveAvatar(contact) {
  if (!contact) return null;
  const resolved = resolveAvatarCanonical(contact);
  const fallback = resolveAvatarCanonical(null);
  return resolved === fallback ? null : resolved;
}

/** URL final para mostrar avatar (relativa → buildAssetUrl; absoluta → con ?v=) */
export function getAvatarDisplayUrl(contact) {
  const raw = resolveAvatar(contact);
  if (!raw) return null;
  return construirUrlImagen(raw, false);
}

/**
 * Nombre visible para fila de usuario bloqueado (alias → nick/username → nombres → email).
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {string}
 */
export function formatBlockedUserPrimaryLabel(row) {
  if (!row || typeof row !== 'object') return '';
  const keys = ['alias', 'alias_personalizado', 'nick', 'username', 'nombre_real', 'nombre', 'email'];
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (!Object.prototype.hasOwnProperty.call(row, k)) continue;
    const v = row[k];
    if (v == null) continue;
    const s = String(v).trim();
    if (s !== '') return s;
  }
  return '';
}

/**
 * País / región si viene en el payload (GET bloqueos u optimista); vacío si no hay dato seguro.
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {string}
 */
export function formatBlockedUserCountryDisplay(row) {
  if (!row || typeof row !== 'object') return '';
  const keys = [
    'pais_nombre',
    'paisNombre',
    'country_name',
    'countryName',
    'pais',
    'country',
    'country_code',
    'countryCode',
    'codigo_pais',
    'iso_pais',
    'iso_pais_nombre',
  ];
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (!Object.prototype.hasOwnProperty.call(row, k)) continue;
    const v = row[k];
    if (v == null) continue;
    const s = String(v).trim();
    if (s !== '') return s;
  }
  return '';
}

/**
 * Etiqueta de país legible para UI (nombre largo o ISO vía Intl.DisplayNames).
 * @param {Record<string, unknown>|null|undefined} row
 * @param {string} [uiLang] - idioma UI (es, en, …)
 * @returns {string} vacío si no hay dato fiable
 */
export function formatBlockedUserCountryLabel(row, uiLang = 'es') {
  if (!row || typeof row !== 'object') return '';
  const nameKeys = ['pais_nombre', 'paisNombre', 'country_name', 'countryName', 'pais', 'country'];
  for (let i = 0; i < nameKeys.length; i += 1) {
    const k = nameKeys[i];
    if (!Object.prototype.hasOwnProperty.call(row, k)) continue;
    const v = row[k];
    if (v == null) continue;
    const s = String(v).trim();
    if (s !== '' && !/^[A-Za-z]{2}$/.test(s)) return s;
  }
  const codeRaw =
    row.codigo_pais ??
    row.country_iso ??
    row.countryIso ??
    row.country_code ??
    row.countryCode ??
    null;
  const cc = String(codeRaw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 2);
  if (cc.length !== 2) return '';
  try {
    const loc = appLangToBcp47(uiLang);
    const dn = new Intl.DisplayNames([loc, 'en-US'], { type: 'region' });
    const label = dn.of(cc);
    return label && String(label).trim() !== '' ? String(label).trim() : cc;
  } catch {
    return cc;
  }
}

/** Código de país para bandera según idioma y país */
export function getCountryCodeForFlag(langCode, countryName = '') {
  if (!langCode) return null;
  const code = langCode.toLowerCase().trim();
  const country = countryName?.toLowerCase().trim() || '';
  if (code === 'es') {
    if (country.includes('argentina')) return 'ar';
    if (country.includes('mexico') || country.includes('méxico')) return 'mx';
    if (country.includes('colombia')) return 'co';
    if (country.includes('chile')) return 'cl';
    if (country.includes('peru') || country.includes('perú')) return 'pe';
    if (country.includes('venezuela')) return 've';
    if (country.includes('ecuador')) return 'ec';
    if (country.includes('uruguay')) return 'uy';
    if (country.includes('paraguay')) return 'py';
    if (country.includes('bolivia')) return 'bo';
    if (country.includes('cuba')) return 'cu';
    return 'es';
  }
  if (code === 'pt') {
    if (country.includes('portugal')) return 'pt';
    return 'br';
  }
  if (code === 'en') {
    if (country.includes('reino unido') || country.includes('united kingdom') || country.includes('england') || country.includes('inglaterra')) return 'gb';
    if (country.includes('australia')) return 'au';
    if (country.includes('canada') || country.includes('canadá')) return 'ca';
    return 'us';
  }
  const langToCountry = { fr: 'fr', de: 'de', it: 'it', ru: 'ru', zh: 'cn', ja: 'jp', ko: 'kr', ar: 'sa', hi: 'in', nl: 'nl', us: 'us', sv: 'se', br: 'br' };
  return langToCountry[code] || null;
}

/**
 * Indica si un string parece una dirección de correo (no usar como nombre visible en lista).
 * @param {unknown} value
 * @returns {boolean}
 */
function looksLikeEmail(value) {
  const s = String(value ?? '').trim();
  if (!s || !s.includes('@')) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

/**
 * Nombre en lista de contactos (vista principal): alias personalizado → nick → username → nombre → nombre_display.
 * Nunca devuelve email; descarta valores que parezcan correo.
 * @param {Record<string, unknown>|null|undefined} contact
 * @returns {string}
 */
export function getContactPrimaryDisplayLabel(contact) {
  if (!contact || typeof contact !== 'object') return '';
  const pick = (v) => {
    const s = String(v ?? '').trim();
    if (!s || looksLikeEmail(s)) return '';
    return s;
  };
  const alias = pick(contact.alias);
  if (alias) return alias;
  const nick = pick(contact.nick);
  if (nick) return nick;
  const username = pick(contact.username);
  if (username) return username;
  const nombre = pick(contact.nombre);
  if (nombre) return nombre;
  const nombreDisplay = pick(contact.nombre_display);
  if (nombreDisplay) return nombreDisplay;
  return '';
}

/** Tipo canónico de contacto (contact, invitation_sent, invitation_received, conversation) */
export function getNormalizedContactType(contact) {
  if (!contact) return 'contact';
  if (typeof contact.type === 'string' && contact.type.trim() !== '') return contact.type;
  const tipo = String(contact.tipo || '').toLowerCase();
  const status = String(contact.status || '').toLowerCase();
  if (tipo === 'contact') return 'contact';
  if (tipo === 'invitation_sent') return 'invitation_sent';
  if (tipo === 'invitation_received') return 'invitation_received';
  if (tipo === 'conversation' || tipo === 'conversacion') return 'conversation';
  if ((tipo === 'invitacion_pendiente' || tipo === 'invitacion_enviada') && status === 'pending') return 'invitation_sent';
  if (tipo === 'invitacion_recibida' && status === 'pending') return 'invitation_received';
  return 'contact';
}

/** Email técnico de invitación (test.local) */
export function isTechnicalInvitationEmail(value) {
  const email = String(value || '').toLowerCase().trim();
  if (!email) return false;
  if (email.endsWith('@test.local')) return true;
  return /^v2-invite-.*@test\.local$/.test(email);
}

/** Valor es un tipo canónico */
export function isCanonicalType(value) {
  return ['contact', 'invitation_sent', 'invitation_received', 'conversation'].includes(String(value || '').toLowerCase().trim());
}

/** Convertir valor a timestamp numérico */
export function toTimestamp(value) {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : 0;
}
